import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Text,
  Box,
  Flex,
  HStack,
  Icon,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Divider,
} from "@chakra-ui/react";
import { FaFile, FaFilePdf, FaFileImage, FaFileWord, FaFileExcel, FaDownload, FaEye, FaSearch, FaCalendarAlt } from "react-icons/fa";
import { FiFolder, FiInbox } from "react-icons/fi";
import { createLogger } from "utils/logger";
import { transformarFecha } from "utils/site";

const FILE_TYPE_STYLE = {
  pdf:    { icon: FaFilePdf,   bg: 'rose.bg', ink: 'rose.ink' },
  jpg:    { icon: FaFileImage, bg: 'mint.bg', ink: 'mint.ink' },
  jpeg:   { icon: FaFileImage, bg: 'mint.bg', ink: 'mint.ink' },
  png:    { icon: FaFileImage, bg: 'mint.bg', ink: 'mint.ink' },
  gif:    { icon: FaFileImage, bg: 'mint.bg', ink: 'mint.ink' },
  webp:   { icon: FaFileImage, bg: 'mint.bg', ink: 'mint.ink' },
  doc:    { icon: FaFileWord,  bg: 'sky.bg',  ink: 'sky.ink'  },
  docx:   { icon: FaFileWord,  bg: 'sky.bg',  ink: 'sky.ink'  },
  xls:    { icon: FaFileExcel, bg: 'teal.bg', ink: 'teal.ink' },
  xlsx:   { icon: FaFileExcel, bg: 'teal.bg', ink: 'teal.ink' },
};
const DEFAULT_FILE_STYLE = { icon: FaFile, bg: 'cream.bg2', ink: 'ink.400' };

const Files = ({ title, data, customer, onFileClick, onRefresh, excludeCategories = [] }) => {
  const logger = useMemo(() => createLogger('Files'), []);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedFormat, setSelectedFormat] = useState('all');

  // Función para detectar archivos de formulario/firma que no deben mostrarse
  const isFormFile = (item) => {
    if (!item.user_id) return true;
    if (item.assign_to_user === false || item.assign_to_user === "false") return true;
    return false;
  };

  // Filtrar archivos: excluir formularios/firmas y categorías restringidas por rol
  const getFilteredFiles = () => {
    if (!data?.data) return [];
    return data.data.filter(item => {
      if (isFormFile(item)) return false;
      if (excludeCategories.length > 0) {
        const cat = (item.survey?.category || item.category || '').toLowerCase();
        if (excludeCategories.some(ec => cat === ec.toLowerCase() || cat.startsWith(ec.toLowerCase()))) {
          return false;
        }
      }
      return true;
    });
  };

  // Lógica de filtrado y ordenamiento
  const filteredAndSortedFiles = useMemo(() => {
    const rawFiles = getFilteredFiles();
    if (!rawFiles.length) return [];

    let filtered = rawFiles;

    if (searchTerm) {
      filtered = filtered.filter(item => {
        try {
          const parsedFilenames = JSON.parse(item.filenames);
          const filename = parsedFilenames[0];
          const fileNameOnly = filename.replace(/\\/g, '').split("/").pop();
          const fileFormat = fileNameOnly.split(".").pop().toLowerCase();

          return fileNameOnly.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 fileFormat.includes(searchTerm.toLowerCase());
        } catch (error) {
          return false;
        }
      });
    }

    if (selectedFormat !== 'all') {
      filtered = filtered.filter(item => {
        try {
          const parsedFilenames = JSON.parse(item.filenames);
          const filename = parsedFilenames[0];
          const fileNameOnly = filename.replace(/\\/g, '').split("/").pop();
          const fileFormat = fileNameOnly.split(".").pop().toLowerCase();

          return fileFormat === selectedFormat;
        } catch (error) {
          return false;
        }
      });
    }

    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          try {
            const aParsed = JSON.parse(a.filenames);
            const bParsed = JSON.parse(b.filenames);
            aValue = aParsed[0].replace(/\\/g, '').split("/").pop().toLowerCase();
            bValue = bParsed[0].replace(/\\/g, '').split("/").pop().toLowerCase();
          } catch (error) {
            aValue = '';
            bValue = '';
          }
          break;
        case 'format':
          try {
            const aParsed = JSON.parse(a.filenames);
            const bParsed = JSON.parse(b.filenames);
            aValue = aParsed[0].replace(/\\/g, '').split(".").pop().toLowerCase();
            bValue = bParsed[0].replace(/\\/g, '').split(".").pop().toLowerCase();
          } catch (error) {
            aValue = '';
            bValue = '';
          }
          break;
        default: // created_at
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
      }

      if (sortBy === 'created_at') {
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      } else {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'desc' ? -comparison : comparison;
      }
    });

    return filtered;
  }, [data, searchTerm, sortBy, sortOrder, selectedFormat]);

  // Formatos disponibles
  const availableFormats = useMemo(() => {
    const rawFiles = getFilteredFiles();
    if (!rawFiles.length) return [];

    const formats = [...new Set(rawFiles.map(item => {
      try {
        const parsedFilenames = JSON.parse(item.filenames);
        const filename = parsedFilenames[0];
        const fileNameOnly = filename.replace(/\\/g, '').split("/").pop();
        return fileNameOnly.split(".").pop().toLowerCase();
      } catch (error) {
        return 'unknown';
      }
    }))].filter(format => format !== 'unknown');

    return formats.sort();
  }, [data]);

  useEffect(() => {
    logger.log('Files component received data', { length: data?.data?.length || 0, filtered: filteredAndSortedFiles.length });
  }, [data, filteredAndSortedFiles, logger]);

  const getFileStyle = (fileFormat) => FILE_TYPE_STYLE[fileFormat?.toLowerCase()] ?? DEFAULT_FILE_STYLE;

  const handleViewFile = (item) => {
    if (onFileClick) {
      try {
        const parsedFilenames = JSON.parse(item.filenames);
        const filename = parsedFilenames[0];
        const cleanFilename = filename.replace(/\\/g, '');

        const fileData = {
          ...item,
          filename: cleanFilename,
          fileUrl: `${process.env.REACT_APP_SURVEY_API_URL || 'https://datofy.cl'}${cleanFilename}`,
          fileName: cleanFilename.split("/").pop()
        };

        onFileClick(fileData);
      } catch (error) {
        logger.error('Error preparing file data', error);
      }
    }
  };

  const handleDownloadFile = (item) => {
    try {
      const parsedFilenames = JSON.parse(item.filenames);
      const filename = parsedFilenames[0];

      const cleanFilename = filename.replace(/\\/g, '');
      const fileUrl = `${process.env.REACT_APP_SURVEY_API_URL || 'https://datofy.cl'}${cleanFilename}`;
      const fileNameOnly = cleanFilename.split("/").pop();

      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileNameOnly;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      logger.error('Error downloading file', error);
    }
  };

  const filteredFiles = getFilteredFiles();

  return (
    <Box
      fontFamily="jakarta"
      bg="white" border="1px solid" borderColor="cream.line" borderRadius="20px"
      p={{ base: 4, md: 6 }} my={4} boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
    >
      {/* ── Header ── */}
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Flex w="34px" h="34px" borderRadius="10px" bg="sky.bg" align="center" justify="center">
            <Icon as={FiFolder} color="sky.ink" boxSize={4} />
          </Flex>
          <Text fontWeight="800" fontSize="15px" color="ink.900">{title}</Text>
          {filteredAndSortedFiles.length > 0 && (
            <Box as="span" px="9px" py="2px" borderRadius="999px" bg="coral.soft" color="coral.700"
              fontSize="11.5px" fontWeight="700">
              {filteredAndSortedFiles.length} de {filteredFiles.length}
            </Box>
          )}
        </HStack>
      </HStack>

      {/* ── Controles de filtrado ── */}
      {filteredFiles.length > 0 && (
        <VStack spacing={3} align="stretch" mb={4}>
          <InputGroup maxW="400px">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="ink.400" boxSize="13px" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nombre o formato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              h="38px" borderRadius="10px" fontSize="13.5px"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.900"
              _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            />
          </InputGroup>

          <HStack spacing={3} flexWrap="wrap">
            <Select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              maxW="190px" h="36px" borderRadius="9px" fontSize="13px" fontWeight="600"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            >
              <option value="all">Todos los formatos</option>
              {availableFormats.map(format => (
                <option key={format} value={format}>{format.toUpperCase()}</option>
              ))}
            </Select>

            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              maxW="170px" h="36px" borderRadius="9px" fontSize="13px" fontWeight="600"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            >
              <option value="created_at">Fecha de creación</option>
              <option value="name">Nombre</option>
              <option value="format">Formato</option>
            </Select>

            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              maxW="140px" h="36px" borderRadius="9px" fontSize="13px" fontWeight="600"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
            >
              <option value="desc">Descendente</option>
              <option value="asc">Ascendente</option>
            </Select>
          </HStack>

          <Divider borderColor="cream.line" />
        </VStack>
      )}

      {/* ── Estados vacíos ── */}
      {!filteredFiles.length && data?.success && (
        <Box bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2" p={8} textAlign="center">
          <Icon as={FiInbox} boxSize={9} color="ink.400" mb={3} />
          <Text fontSize="13.5px" color="ink.700" fontWeight="700">No hay documentos registrados</Text>
          <Text fontSize="12.5px" color="ink.400" mt={1}>Los documentos aparecerán aquí cuando se suban</Text>
        </Box>
      )}

      {data && !data.success && (
        <Box bg="butter.bg" borderRadius="16px" p={8} textAlign="center">
          <Icon as={FaFile} boxSize={9} color="butter.ink" mb={3} />
          <Text fontSize="13.5px" color="butter.ink" fontWeight="700">Esta cuenta no registra actividad</Text>
          <Text fontSize="12.5px" color="butter.ink" mt={1} opacity={0.8}>
            No se encontraron documentos asociados a este usuario
          </Text>
        </Box>
      )}

      {filteredFiles.length > 0 && filteredAndSortedFiles.length === 0 && (
        <Box bg="sky.bg" borderRadius="16px" p={8} textAlign="center">
          <Icon as={FaSearch} boxSize={8} color="sky.ink" mb={3} />
          <Text fontSize="13.5px" color="sky.ink" fontWeight="700">No se encontraron archivos</Text>
          <Text fontSize="12.5px" color="sky.ink" mt={1} opacity={0.8}>Intenta modificar los filtros de búsqueda</Text>
        </Box>
      )}

      {/* ── Lista de archivos ── */}
      {filteredAndSortedFiles.length > 0 && (
        <VStack spacing={2} w="100%" align="stretch" maxHeight="600px" overflowY="auto" pr={1}>
          {filteredAndSortedFiles.map((item, index) => {
            let fileNameOnly, fileFormat;
            try {
              const parsedFilenames = JSON.parse(item.filenames);
              const cleanFilename = parsedFilenames[0].replace(/\\/g, '');
              fileNameOnly = cleanFilename.split("/").pop();
              fileFormat = fileNameOnly.split(".").pop().toLowerCase();
            } catch (error) {
              fileNameOnly = 'Archivo';
              fileFormat = 'unknown';
            }

            const created_at = transformarFecha(item.created_at);
            const { icon: FileIcon, bg: iconBg, ink: iconInk } = getFileStyle(fileFormat);

            return (
              <Flex
                key={index}
                align="center" gap="14px"
                p={3} borderRadius="14px"
                border="1px solid" borderColor="cream.line"
                _hover={{ bg: 'coral.tint', borderColor: 'coral.soft' }}
                transition="background .13s, border-color .13s"
              >
                <Flex
                  w="44px" h="44px" borderRadius="12px" flexShrink={0}
                  bg={iconBg} align="center" justify="center"
                >
                  <Icon as={FileIcon} color={iconInk} boxSize={5} />
                </Flex>

                <Box flex={1} minW={0}>
                  <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>
                    {fileNameOnly}
                  </Text>
                  <HStack spacing={2} mt="2px">
                    <Icon as={FaCalendarAlt} boxSize="10px" color="ink.400" />
                    <Text fontSize="11.5px" color="ink.400" fontWeight="500">{created_at}</Text>
                    <Box as="span" px="7px" py="1px" borderRadius="999px" bg="cream.bg2" color="ink.700"
                      fontSize="10px" fontWeight="700">
                      {fileFormat.toUpperCase()}
                    </Box>
                  </HStack>
                </Box>

                <HStack spacing={1} flexShrink={0}>
                  <Button
                    size="sm" variant="ghost" color="ink.700" fontWeight="700" fontSize="12.5px"
                    leftIcon={<Icon as={FaEye} boxSize="12px" />}
                    _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                    onClick={(e) => { e.stopPropagation(); handleViewFile(item); }}
                  >
                    Ver
                  </Button>
                  <Button
                    size="sm" variant="ghost" color="ink.700" fontWeight="700" fontSize="12.5px"
                    leftIcon={<Icon as={FaDownload} boxSize="12px" />}
                    _hover={{ bg: 'mint.bg', color: 'mint.ink' }}
                    onClick={(e) => { e.stopPropagation(); handleDownloadFile(item); }}
                  >
                    Descargar
                  </Button>
                </HStack>
              </Flex>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default Files;
